
import { Control } from "react-hook-form";
import { 
  FormField, 
  FormItem, 
  FormLabel, 
  FormControl, 
  FormMessage 
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from "@/components/ui/select";
import { DateInput } from "@/components/ui/date-input";
import { countryCodes, ClientFormValues, getPlaceholder, formatPhoneNumber } from "../form-utils/clientFormUtils";

interface PersonalInfoFormProps {
  control: Control<ClientFormValues>;
  onPhoneChange: (value: string) => void;
  onDateChange: (date: Date | null) => void;
}

export function PersonalInfoForm({ control, onPhoneChange, onDateChange }: PersonalInfoFormProps) {
  return (
    <div className="grid grid-cols-2 gap-6">
      <FormField
        control={control}
        name="fullName"
        render={({ field }) => (
          <FormItem>
            <FormLabel className="text-white font-medium">Full Name *</FormLabel>
            <FormControl>
              <Input 
                placeholder="John Doe" 
                {...field} 
                className="bg-black/20 border-black/20 text-white placeholder:text-white/60 focus:border-white/40 focus:ring-white/10" 
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={control}
        name="email"
        render={({ field }) => (
          <FormItem>
            <FormLabel className="text-white font-medium">Email Address *</FormLabel>
            <FormControl>
              <Input 
                placeholder="client@example.com" 
                {...field} 
                className="bg-black/20 border-black/20 text-white placeholder:text-white/60 focus:border-white/40 focus:ring-white/10" 
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={control}
        name="countryCode"
        render={({ field }) => (
          <FormItem className="flex flex-col">
            <FormLabel className="text-white font-medium">Phone Number (Optional)</FormLabel>
            <div className="flex gap-2">
              <Select 
                value={field.value}
                onValueChange={field.onChange}
              >
                <FormControl>
                  <SelectTrigger className="w-[110px] bg-black/20 border-black/20 text-white">
                    <SelectValue placeholder="Code" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent className="max-h-[300px]">
                  {countryCodes.map((country) => (
                    <SelectItem key={country.code} value={country.code}>
                      {country.country} {country.code}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormField
                control={control}
                name="phone"
                render={({ field: phoneField }) => (
                  <FormItem className="flex-1">
                    <FormControl>
                      <Input
                        type="tel"
                        placeholder={getPlaceholder(field.value)}
                        value={phoneField.value}
                        onChange={(e) => {
                          const formatted = formatPhoneNumber(e.target.value, field.value);
                          onPhoneChange(formatted);
                        }}
                        className="bg-black/20 border-black/20 text-white placeholder:text-white/60 focus:border-white/40 focus:ring-white/10"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </FormItem>
        )}
      />

      <FormField
        control={control}
        name="gender"
        render={({ field }) => (
          <FormItem>
            <FormLabel className="text-white font-medium">Gender *</FormLabel>
            <Select
              onValueChange={field.onChange}
              defaultValue={field.value}
            >
              <FormControl>
                <SelectTrigger className="bg-black/20 border-black/20 text-white">
                  <SelectValue placeholder="Select gender" />
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                <SelectItem value="male">Male</SelectItem>
                <SelectItem value="female">Female</SelectItem>
                <SelectItem value="other">Not Specified</SelectItem>
              </SelectContent>
            </Select>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={control}
        name="dateOfBirth"
        render={({ field }) => (
          <FormItem className="flex flex-col">
            <FormLabel className="text-white font-medium">Date of Birth *</FormLabel>
            <FormControl>
              <DateInput 
                onDateChange={onDateChange}
                className="bg-black/20 border-black/20 text-white placeholder:text-white/60 focus:border-white/40 focus:ring-white/10"
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
    </div>
  );
}
